// --- src/services/adminService.ts ---
import { supabase } from '@/lib/supabaseClient';
import { LogItem } from '../components/types';
import { ScanMode } from '../components/types';
import { logger } from './logger';
import { safeParseOfflineUser } from '../utils/safeParse';

// Queue item interface for offline synchronization (needed for logAction)
interface QueueItem {
  id: string;
  type: 'updateItemQuantity' | 'logAction';
  timestamp: number;
  data: {
    // For updateItemQuantity
    ean?: string;
    delta?: number;
    productDetails?: any;
    reason?: ScanMode;
    // For logAction
    actionType?: string;
    details?: string;
  };
}

export const adminService = {
  // ===== LOGGING FUNCTIONS =====

  /**
   * Logs an action to the inventory_logs table
   * Supports offline mode by queuing actions when offline
   * @param actionType - Type of action (e.g., 'COUNT', 'SALE', 'IMPORT')
   * @param details - Details of the action
   * @param isSyncing - Whether this is being called from the sync queue
   * @param addToQueue - Optional function to add items to sync queue (from inventoryService)
   */
  async logAction(
    actionType: string,
    details: string,
    isSyncing: boolean = false,
    addToQueue?: (item: Omit<QueueItem, 'id' | 'timestamp'>) => void
  ): Promise<void> {
    // CRITICAL: Check network status first - if offline and not syncing, queue immediately
    if (!navigator.onLine && !isSyncing && addToQueue) {
      addToQueue({
        type: 'logAction',
        data: {
          actionType,
          details,
        },
      });
      console.log('Offline detected - added logAction to sync queue immediately');
      return; // Don't throw, logging failures shouldn't break operations
    }

    try {
      // Get user with offline fallback
      let user = null;
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (data?.user) {
          user = data.user;
        }
      } catch (error) {
        // Auth check failed, try cached user
      }

      // Fallback to cached user if auth failed (offline mode)
      if (!user) {
        const cachedUserStr = localStorage.getItem('offline_user');
        const cachedUser = safeParseOfflineUser(cachedUserStr);
        if (cachedUser) {
          user = { id: cachedUser.id } as any;
        }
      }

      if (!user) {
        // If no user but we're not syncing, queue the operation
        if (!isSyncing && addToQueue) {
          addToQueue({
            type: 'logAction',
            data: {
              actionType,
              details,
            },
          });
          console.log('No user session - added logAction to sync queue');
        }
        return; // Don't throw, logging failures shouldn't break operations
      }

      const { error } = await supabase.from('inventory_logs').insert({
        user_id: user.id,
        action_type: actionType,
        details: details,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      // If not syncing and ANY error occurred (including Auth/Token errors), add to queue
      if (!isSyncing && addToQueue) {
        addToQueue({
          type: 'logAction',
          data: {
            actionType,
            details,
          },
        });
        console.log('Error occurred - added logAction to sync queue:', error);
      }
      // Don't re-throw for logAction - logging failures shouldn't break the calling operation
      logger.warn('logAction failed (queued for retry)', error, 'AdminService');
    }
  },

  /**
   * Gets inventory logs with optional filtering
   * Company-wide logs (RLS policy handles security)
   * @param filterType - Optional filter by action type (e.g., 'COUNT', 'SALE', 'ALL')
   * @returns Array of log items with user names
   */
  async getLogs(filterType?: string): Promise<LogItem[]> {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) return [];

    try {
      // Get company-wide logs (RLS policy ensures company-level access)
      let query = supabase
        .from('inventory_logs')
        .select('id, action_type, details, created_at, user_id')
        .order('created_at', { ascending: false })
        .limit(50);

      // Ha van szűrő, és az NEM 'ALL', akkor szűrünk rá
      if (filterType && filterType !== 'ALL') {
        query = query.eq('action_type', filterType);
      }

      const { data: logs, error: logsError } = await query;

      if (logsError) {
        logger.error('Error fetching logs', logsError, 'AdminService');
        return [];
      }

      if (!logs || logs.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(logs.map((log) => log.user_id))];

      // Fetch user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (profilesError) {
        logger.error('Error fetching profiles', profilesError, 'AdminService');
      }

      // Create a map of user_id -> full_name
      const userMap = new Map<string, string>();
      (profiles || []).forEach((profile) => {
        userMap.set(profile.id, profile.full_name);
      });

      // Combine logs with user names
      return logs.map((log) => ({
        id: log.id,
        action_type: log.action_type,
        details: log.details,
        created_at: log.created_at,
        user_name: userMap.get(log.user_id) || 'Ismeretlen',
      }));
    } catch (error) {
      logger.error('Error in getLogs', error, 'AdminService');
      return [];
    }
  },

  /**
   * Get recent transaction items with prices (for ScannerInterface history)
   * @param limit - Maximum number of transactions to return
   * @returns Array of recent transaction items
   */
  async getRecentTransactions(limit: number = 5) {
    // Get user with offline fallback (same as posService and syncService)
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) return [];

    try {
      // Get transactions for the current user
      const { data: transactions, error: transactionsError } = await supabase
        .from('inventory_transactions')
        .select('id, type')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (transactionsError) {
        logger.error('Error fetching transactions', transactionsError, 'AdminService');
        return [];
      }

      if (!transactions || transactions.length === 0) return [];

      const transactionIds = transactions.map((t) => t.id);
      const transactionTypeMap = new Map(transactions.map((t) => [t.id, t.type]));

      // Get transaction items for these transactions
      const { data: items, error: itemsError } = await supabase
        .from('inventory_transaction_items')
        .select(
          'id, ean, quantity_change, snapshot_price, snapshot_selling_price, product_name_snapshot, created_at, transaction_id'
        )
        .in('transaction_id', transactionIds)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (itemsError) {
        logger.error('Error fetching transaction items', itemsError, 'AdminService');
        return [];
      }

      return (items || []).map((item) => {
        // Extract transaction type and raw quantity
        const transactionType = transactionTypeMap.get(item.transaction_id) || 'COUNT';
        const rawQty = Number(item.quantity_change);

        // BUSINESS LOGIC NORMALIZATION: Enforce correct sign based on transaction type
        let finalQty: number;

        if (transactionType === 'RETURN') {
          // RETURN = Stock INCREASES = MUST be POSITIVE
          // Force positive regardless of how backend stores it
          finalQty = Math.abs(rawQty);
        } else if (transactionType === 'SALE') {
          // SALE = Stock DECREASES = MUST be NEGATIVE
          // Force negative regardless of how backend stores it
          finalQty = -Math.abs(rawQty);
        } else {
          // COUNT, SCRAP, INTERNAL: Respect the raw sign (can be +/-)
          finalQty = rawQty;
        }

        return {
          id: item.id,
          ean: item.ean,
          quantity_change: finalQty, // Normalized value with correct sign
          price: item.snapshot_price || 0,
          selling_price: item.snapshot_selling_price || 0,
          product_name: item.product_name_snapshot || item.ean,
          transaction_type: transactionType,
          created_at: item.created_at,
        };
      });
    } catch (error) {
      logger.error('Error in getRecentTransactions', error, 'AdminService');
      return [];
    }
  },

  /**
   * Get recent activity logs with user names (for Dashboard)
   * @param limit - Maximum number of activity logs to return
   * @returns Array of recent activity logs with user names
   */
  async getRecentActivity(limit: number = 5) {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) return [];

    try {
      // Get recent logs (all users in the system, not just current user)
      const { data: logs, error: logsError } = await supabase
        .from('inventory_logs')
        .select('id, action_type, details, created_at, user_id')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (logsError) {
        logger.error('Error fetching logs', logsError, 'AdminService');
        return [];
      }

      if (!logs || logs.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(logs.map((log) => log.user_id))];

      // Fetch user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (profilesError) {
        logger.error('Error fetching profiles', profilesError, 'AdminService');
      }

      // Create a map of user_id -> full_name
      const userMap = new Map<string, string>();
      (profiles || []).forEach((profile) => {
        userMap.set(profile.id, profile.full_name);
      });

      // Combine logs with user names
      return logs.map((log) => ({
        id: log.id,
        action_type: log.action_type,
        details: log.details,
        created_at: log.created_at,
        user_name: userMap.get(log.user_id) || 'Ismeretlen',
      }));
    } catch (error) {
      logger.error('Error in getRecentActivity', error, 'AdminService');
      return [];
    }
  },

  // ===== SUPPORT TICKET FUNCTIONS =====

  /**
   * Submits a support ticket to the database and sends it to n8n webhook
   * @param ticket - Ticket information
   */
  async submitTicket(ticket: {
    page: string;
    process: string;
    description: string;
    error?: string;
  }): Promise<void> {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) throw new Error('Nincs bejelentkezve');

    // 1. Mentés az adatbázisba (hogy legyen nyoma az appban is)
    const { data, error } = await supabase
      .from('tickets')
      .insert({
        user_id: user.id,
        page: ticket.page,
        process: ticket.process,
        description: ticket.description,
        error_message: ticket.error || '',
        status: 'OPEN',
      })
      .select()
      .single();

    if (error) throw error;

    // 2. Küldés az n8n-nek (Webhook)
    // Ezt az URL-t majd az n8n-ből másolod ki!
    const N8N_WEBHOOK_URL =
      'https://n8n.greenrevolution.hu/webhook/86f08809-1e18-4395-aad7-89a0576282d2';

    // Nem várjuk meg a választ (fire and forget), hogy ne lassítsa az appot
    fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...ticket,
        user_email: user.email,
        ticket_id: data.id,
        timestamp: new Date().toISOString(),
      }),
    }).catch((err) => logger.error('n8n hiba', err, 'AdminService'));
  },

  /**
   * Gets the current user's support tickets
   * @returns Array of user's tickets
   */
  async getMyTickets() {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) return [];

    const { data } = await supabase
      .from('tickets')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5); // Elég az utolsó 5 db

    return data || [];
  },
};
